import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import BankLogo from '../components/BankLogo';
import { ArrowLeft, Upload, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const ApplicationPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    tenure_months: '',
    purpose: '',
    employment_type: 'salaried',
    annual_income: '',
    existing_loans: ''
  });

  const [documents, setDocuments] = useState({
    identity_proof: null,
    address_proof: null,
    income_proof: null,
    bank_statement: null
  });

  useEffect(() => {
    fetchData();
  }, [serviceId]);

  const fetchData = async () => {
    try {
      const [servicesRes, banksRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/banks`)
      ]);
      
      const selectedService = servicesRes.data.find(s => s.id === serviceId);
      if (!selectedService) {
        toast.error('Service not found');
        navigate('/services');
        return;
      }
      
      setService(selectedService);
      setBanks(banksRes.data.filter(bank => 
        bank.services.includes(selectedService.name)
      ));
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const handleSubmitApplication = async () => {
    if (!selectedBank) {
      toast.error('Please select a bank');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        service_id: serviceId,
        bank_id: selectedBank.id,
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        tenure_months: formData.tenure_months ? parseInt(formData.tenure_months) : null,
        annual_income: parseFloat(formData.annual_income)
      };

      const response = await axios.post(`${API}/applications`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` }
      });
      
      setApplicationId(response.data.id);
      toast.success('Application submitted successfully!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadDocument = async (docType, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(
        `${API}/applications/${applicationId}/documents?document_type=${docType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('arthik_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setDocuments(prev => ({ ...prev, [docType]: file }));
      toast.success(`${docType.replace('_', ' ')} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${docType.replace('_', ' ')}`);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
        <div style={{ color: '#FF6B35', fontSize: '1.125rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      {/* Application Form */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '4rem 2rem' }}>
        <button 
          onClick={() => navigate('/services')}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            background: 'none', 
            border: 'none', 
            color: '#FF6B35', 
            cursor: 'pointer',
            marginBottom: '2rem',
            fontWeight: '600'
          }}
          data-testid="back-to-services-button"
        >
          <ArrowLeft size={20} /> Back to Services
        </button>

        <div style={{ backgroundColor: 'white', borderRadius: '1.5rem', padding: '3rem', border: '1px solid #E2E8F0' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem', color: '#0F172A' }}>
            Apply for {service?.name}
          </h1>
          <p style={{ color: '#475569', marginBottom: '3rem' }}>
            Complete the application in 3 simple steps
          </p>

          {/* Progress Indicator */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '3rem' }}>
            {[
              { num: 1, label: 'Bank Selection' },
              { num: 2, label: 'Application Details' },
              { num: 3, label: 'Upload Documents' }
            ].map((item) => (
              <div key={item.num} style={{ flex: 1, textAlign: 'center' }}>
                <div 
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: step >= item.num ? '#FF6B35' : '#E2E8F0',
                    color: step >= item.num ? 'white' : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    margin: '0 auto 0.5rem'
                  }}
                  data-testid={`step-indicator-${item.num}`}
                >
                  {step > item.num ? <CheckCircle size={20} /> : item.num}
                </div>
                <div style={{ fontSize: '0.875rem', color: step >= item.num ? '#0F172A' : '#475569', fontWeight: step >= item.num ? '600' : '400' }}>
                  {item.label}
                </div>
              </div>
            ))}
          </div>

          {/* Step 1: Bank Selection */}
          {step === 1 && (
            <div className="fade-in" data-testid="step-1-bank-selection">
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
                Select Your Preferred Bank
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                {banks.map((bank) => (
                  <div
                    key={bank.id}
                    className={`bank-card ${selectedBank?.id === bank.id ? 'selected' : ''}`}
                    onClick={() => setSelectedBank(bank)}
                    data-testid={`bank-card-${bank.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div style={{ marginBottom: '1rem' }}>
                      <BankLogo bankName={bank.name} size={70} />
                    </div>
                    <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#0F172A', marginBottom: '0.5rem' }}>
                      {bank.name}
                    </h3>
                    {bank.interest_rates && bank.interest_rates[service?.name.toLowerCase().replace(/\s+/g, '_')] && (
                      <p style={{ fontSize: '0.875rem', color: '#FF6B35', fontWeight: '600' }}>
                        Rate: {bank.interest_rates[service?.name.toLowerCase().replace(/\s+/g, '_')]}%
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={() => setStep(2)}
                className="primary-button"
                disabled={!selectedBank}
                style={{ opacity: !selectedBank ? 0.5 : 1 }}
                data-testid="next-to-step-2-button"
              >
                Continue to Application
              </button>
            </div>
          )}

          {/* Step 2: Application Form */}
          {step === 2 && (
            <div className="fade-in" data-testid="step-2-application-form">
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1.5rem', color: '#0F172A' }}>
                Application Details
              </h2>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {service?.category === 'loan' && (
                  <>
                    <div>
                      <label className="label-text">Loan Amount (₹)</label>
                      <input
                        type="number"
                        className="input-field"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        placeholder="Enter amount"
                        required
                        data-testid="loan-amount-input"
                      />
                    </div>

                    <div>
                      <label className="label-text">Tenure (Months)</label>
                      <input
                        type="number"
                        className="input-field"
                        value={formData.tenure_months}
                        onChange={(e) => setFormData({...formData, tenure_months: e.target.value})}
                        placeholder="Enter tenure"
                        required
                        data-testid="tenure-input"
                      />
                    </div>

                    <div>
                      <label className="label-text">Purpose</label>
                      <textarea
                        className="input-field"
                        value={formData.purpose}
                        onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                        placeholder="Purpose of loan"
                        rows={3}
                        data-testid="purpose-input"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="label-text">Employment Type</label>
                  <select
                    className="input-field"
                    value={formData.employment_type}
                    onChange={(e) => setFormData({...formData, employment_type: e.target.value})}
                    data-testid="employment-type-select"
                  >
                    <option value="salaried">Salaried</option>
                    <option value="self_employed">Self Employed</option>
                    <option value="business">Business Owner</option>
                  </select>
                </div>

                <div>
                  <label className="label-text">Annual Income (₹)</label>
                  <input
                    type="number"
                    className="input-field"
                    value={formData.annual_income}
                    onChange={(e) => setFormData({...formData, annual_income: e.target.value})}
                    placeholder="Enter annual income"
                    required
                    data-testid="annual-income-input"
                  />
                </div>

                <div>
                  <label className="label-text">Existing Loans/EMI (Optional)</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.existing_loans}
                    onChange={(e) => setFormData({...formData, existing_loans: e.target.value})}
                    placeholder="Describe any existing loans"
                    data-testid="existing-loans-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button
                  onClick={() => setStep(1)}
                  className="outline-button"
                  data-testid="back-to-step-1-button"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmitApplication}
                  className="primary-button"
                  disabled={submitting || !formData.annual_income}
                  style={{ flex: 1 }}
                  data-testid="submit-application-button"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Document Upload */}
          {step === 3 && (
            <div className="fade-in" data-testid="step-3-document-upload">
              <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <CheckCircle size={64} style={{ color: '#FF6B35', margin: '0 auto 1rem' }} />
                <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#0F172A' }}>
                  Application Submitted!
                </h2>
                <p style={{ color: '#475569' }}>
                  Application ID: <strong>{applicationId}</strong>
                </p>
              </div>

              <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#0F172A' }}>
                Upload Required Documents
              </h3>

              <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                {Object.entries({
                  identity_proof: 'Identity Proof (Aadhaar/PAN)',
                  address_proof: 'Address Proof',
                  income_proof: 'Income Proof (Salary Slips/ITR)',
                  bank_statement: 'Bank Statement (Last 6 months)'
                }).map(([key, label]) => (
                  <div key={key} style={{ padding: '1.5rem', border: '2px dashed #E2E8F0', borderRadius: '1rem', textAlign: 'center' }}>
                    <FileText size={32} style={{ color: '#16A34A', margin: '0 auto 0.5rem' }} />
                    <div style={{ fontWeight: '600', color: '#0F172A', marginBottom: '0.5rem' }}>
                      {label}
                    </div>
                    {documents[key] ? (
                      <div style={{ color: '#FF6B35', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                        <CheckCircle size={16} /> {documents[key].name}
                      </div>
                    ) : (
                      <label style={{ cursor: 'pointer' }}>
                        <input
                          type="file"
                          style={{ display: 'none' }}
                          onChange={(e) => handleUploadDocument(key, e.target.files[0])}
                          accept=".pdf,.jpg,.jpeg,.png"
                          data-testid={`upload-${key}-input`}
                        />
                        <span className="outline-button" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Upload size={16} /> Choose File
                        </span>
                      </label>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="primary-button"
                  style={{ flex: 1 }}
                  data-testid="go-to-dashboard-button"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => navigate('/services')}
                  className="outline-button"
                  data-testid="apply-another-service-button"
                >
                  Apply for Another Service
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationPage;
